import React, { useState, useEffect } from 'react';
import './ManageUsers.css';

const ManageUsers = () => {
  // --- STATE MANAGEMENT ---
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Form State
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    validityValue: 1,
    validityUnit: 'days'
  });

  // --- HELPER: CALCULATE TIME REMAINING ---
  const calculateTimeRemaining = (expiryTimestamp) => {
    if (!expiryTimestamp) return "Permanent (Admin)";
    
    const now = Math.floor(Date.now() / 1000); // Current Unix time
    const diff = expiryTimestamp - now;

    if (diff <= 0) return "Expired";

    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);

    if (days > 0) return `${days} days, ${hours} hours left`;
    if (hours > 0) return `${hours} hours, ${minutes} mins left`;
    return `${minutes} mins left`;
  };

  // --- API ACTIONS ---

  // 1. Fetch Users on Load
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      // Replace with your GET Lambda endpoint
      const response = await fetch('https://f0kufr6w5f.execute-api.ap-south-1.amazonaws.com/praud/getusers');
      const data = await response.json();
      // Assuming data.users is your array from DynamoDB
      setUsers(data || []); 
    } catch (error) {
      console.error("Failed to fetch users", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 2. Create New User
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      action: 'create_user',
      username: formData.username,
      password: formData.password,
      validity: {
        value: parseInt(formData.validityValue),
        unit: formData.validityUnit
      }
    };

    try {
      // Replace with your POST Lambda endpoint
      const response = await fetch('https://f0kufr6w5f.execute-api.ap-south-1.amazonaws.com/praud/createuser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert("User Created Successfully");
        setIsModalOpen(false); // Close Modal
        fetchUsers(); // Refresh the list
        setFormData({ username: '', password: '', validityValue: 1, validityUnit: 'days' }); // Reset Form
      } else {
        alert("Failed to create user");
      }
    } catch (error) {
      console.error(error);
      alert("Error creating user");
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- RENDER ---
  return (
    <div className="admin-page-container">
      
      {/* HEADER SECTION */}
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p>Monitor access and manage credentials.</p>
        </div>
        <button className="create-btn" onClick={() => setIsModalOpen(true)}>
          + Create New User
        </button>
      </div>

      {/* USER LIST TABLE */}
      <div className="table-container">
        {isLoading ? (
          <p>Loading users...</p>
        ) : (
          <table className="user-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Access Status</th>
                <th>Time Remaining</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const timeRemaining = calculateTimeRemaining(user.expiry_timestamp);
                const isExpired = timeRemaining === "Expired";
                
                return (
                  <tr key={index} className={isExpired ? 'row-expired' : ''}>
                    <td>{user.username}</td>
                    <td><span className={`badge ${user.role.toLowerCase()}`}>{user.role}</span></td>
                    <td>
                      {isExpired ? (
                        <span className="status-dot red">● Expired</span>
                      ) : (
                        <span className="status-dot green">● Active</span>
                      )}
                    </td>
                    <td className="time-text">{timeRemaining}</td>
                    <td>
                      <button className="delete-btn">Revoke</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* --- MODAL (POPUP) --- */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create Temporary User</h3>
              <button className="close-x" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            
            <form onSubmit={handleCreateSubmit}>
              <div className="form-group">
                <label>Username</label>
                <input type="text" name="username" value={formData.username} onChange={handleInputChange} required />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input type="password" name="password" value={formData.password} onChange={handleInputChange} required />
              </div>

              <div className="form-group">
                <label>Access Duration</label>
                <div className="split-inputs">
                  <input type="number" name="validityValue" min="1" value={formData.validityValue} onChange={handleInputChange} />
                  <select name="validityUnit" value={formData.validityUnit} onChange={handleInputChange}>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="save-btn">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManageUsers;